import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './PropertyDetailNew.css';

const PropertyDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedRoomTypeId = searchParams.get('roomType');
  const navigate = useNavigate();
  const { currentUser, token } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [showGallery, setShowGallery] = useState(false);
  const [roomQuantities, setRoomQuantities] = useState({});
  const [bookedForSelf, setBookedForSelf] = useState(true);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  useEffect(() => {
    const fetchHotelDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/hotels/${id}`);
        
        if (!response.ok) {
          throw new Error('Hotel not found');
        }

        const data = await response.json();
        const hotel = data.hotel;

        // Transform data for component
        const transformedProperty = {
          id: hotel.hotel_id || hotel.id,
          name: hotel.name,
          location: `${hotel.city || ''}, ${hotel.country || ''}`.trim().replace(/^,\s*|,\s*$/g, ''),
          category: hotel.category,
          description: hotel.description || 'No description available',
          images: [
            "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800",
            "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800",
            "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800",
            "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800",
            "https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?w=800"
          ],
          amenities: Array.isArray(hotel.facilities) ? hotel.facilities.map(f => ({
            icon: "✓",
            name: f.name
          })) : [],
          rooms: Array.isArray(hotel.room_types) ? hotel.room_types.map(rt => ({
            id: rt.id,
            name: rt.room_type,
            price: rt.price || 0,
            maxGuests: rt.capacity || 2,
            availableRooms: parseInt(rt.available_rooms) || 0,
            description: rt.description || '',
            amenities: Array.isArray(rt.facilities) ? rt.facilities.filter(f => f !== null) : []
          })) : [],
          policies: {
            checkIn: hotel.check_in_time || "15:00",
            checkOut: hotel.check_out_time || "12:00",
            cancellation: "Free cancellation within 48 hours of booking",
            children: "Children of all ages are welcome",
            pets: "Pets are not allowed"
          },
          phone: hotel.phone,
          email: hotel.email,
          website: hotel.website
        };

        setProperty(transformedProperty);

        // Initialize room quantities
        const initialQuantities = {};
        transformedProperty.rooms.forEach(room => {
          initialQuantities[room.id] = 0;
        });
        setRoomQuantities(initialQuantities);

        // Auto-select room type if preselected from URL
        if (preselectedRoomTypeId && transformedProperty.rooms.length > 0) {
          const preselectedRoom = transformedProperty.rooms.find(
            r => r.id === parseInt(preselectedRoomTypeId)
          );
          if (preselectedRoom) {
            setSelectedRoom(preselectedRoom);
            initialQuantities[preselectedRoom.id] = 1;
            setRoomQuantities({...initialQuantities});
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching hotel details:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchHotelDetails();

    // Set default dates
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date();
    dayAfter.setDate(dayAfter.getDate() + 2);
    
    setCheckIn(tomorrow.toISOString().split('T')[0]);
    setCheckOut(dayAfter.toISOString().split('T')[0]);
  }, [id, preselectedRoomTypeId]);

  const handleRoomQuantityChange = (roomId, quantity) => {
    setRoomQuantities(prev => ({
      ...prev,
      [roomId]: parseInt(quantity) || 0
    }));
  };

  const handleBooking = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    const selectedRooms = Object.entries(roomQuantities)
      .filter(([_, quantity]) => quantity > 0)
      .map(([roomId, quantity]) => ({
        room_type_id: parseInt(roomId),
        quantity: quantity
      }));

    if (selectedRooms.length === 0) {
      alert('Please select at least one room');
      return;
    }

    if (!checkIn || !checkOut) {
      alert('Please select check-in and check-out dates');
      return;
    }

    try {
      if (!bookedForSelf && !guestName.trim()) {
        alert('Please enter the guest\'s name');
        return;
      }
      if (!bookedForSelf && !guestEmail.trim()) {
        alert('Please enter the guest\'s email address so they can receive their booking confirmation and login credentials.');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!bookedForSelf && !emailRegex.test(guestEmail.trim())) {
        alert('Please enter a valid guest email address.');
        return;
      }

      const requestBody = {
        hotel_id: property.id,
        check_in_date: checkIn,
        check_out_date: checkOut,
        number_of_guests: guests,
        rooms: selectedRooms,
        booked_for_self: bookedForSelf,
        guest_name: bookedForSelf ? null : guestName.trim(),
        guest_email: bookedForSelf ? null : guestEmail.trim() || null,
        guest_phone: bookedForSelf ? null : guestPhone.trim() || null,
      };

      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        navigate(`/checkout/${result.data.booking_id}`, {
          state: {
            booking: result.data,
            amount: result.data.total_amount
          }
        });
      } else {
        alert(result.message || 'Failed to create booking');
      }
    } catch (error) {
      console.error('Booking error:', error);
      alert('Failed to create booking. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading property details...</p>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="error-container">
        <h2>{error || 'Property not found'}</h2>
        <button onClick={() => navigate('/booking')}>Back to Booking</button>
      </div>
    );
  }

  const totalRoomsSelected = Object.values(roomQuantities).reduce((sum, qty) => sum + qty, 0);
  const totalPrice = property.rooms.reduce((sum, room) => {
    return sum + (room.price * (roomQuantities[room.id] || 0));
  }, 0);

  return (
    <div className="property-detail-v2">
      {/* Full Screen Gallery Modal */}
      {showGallery && (
        <div className="gallery-modal" onClick={() => setShowGallery(false)}>
          <button className="gallery-close" onClick={() => setShowGallery(false)}>
            ×
          </button>
          <div className="gallery-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="gallery-nav prev"
              onClick={() => setSelectedImageIndex(prev => 
                prev === 0 ? property.images.length - 1 : prev - 1
              )}
            >
              ‹
            </button>
            <img 
              src={property.images[selectedImageIndex]} 
              alt={`${property.name} - ${selectedImageIndex + 1}`}
            />
            <button 
              className="gallery-nav next"
              onClick={() => setSelectedImageIndex(prev => 
                (prev + 1) % property.images.length
              )}
            >
              ›
            </button>
            <div className="gallery-counter">
              {selectedImageIndex + 1} / {property.images.length}
            </div>
          </div>
        </div>
      )}

      {/* Photo Gallery Grid */}
      <div className="photo-gallery-grid">
        <div className="main-photo" onClick={() => { setSelectedImageIndex(0); setShowGallery(true); }}>
          <img src={property.images[0]} alt={property.name} />
        </div>
        <div className="side-photos">
          {property.images.slice(1, 5).map((img, index) => (
            <div 
              key={index} 
              className="side-photo"
              onClick={() => { setSelectedImageIndex(index + 1); setShowGallery(true); }}
            >
              <img src={img} alt={`${property.name} ${index + 2}`} />
              {index === 3 && property.images.length > 5 && (
                <div className="more-photos-overlay">
                  <span>+{property.images.length - 5} photos</span>
                </div>
              )}
            </div>
          ))}
        </div>
        <button className="show-all-photos-btn" onClick={() => setShowGallery(true)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
            <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
            <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
            <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Show all photos
        </button>
      </div>

      {/* Property Header */}
      <div className="property-header-v2">
        <div className="container">
          <div className="breadcrumb">
            <span onClick={() => navigate('/')}>Home</span>
            <span>›</span>
            <span onClick={() => navigate('/booking')}>Hotels</span>
            <span>›</span>
            <span>{property.location}</span>
          </div>
          
          <div className="header-content">
            {property.category && (
              <span className="category-badge">{property.category}</span>
            )}
            <h1>{property.name}</h1>
            <div className="location-info">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
              </svg>
              <span>{property.location}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="property-content">
        <div className="container">
          <div className="content-grid">
            {/* Main Column */}
            <div className="main-column">
              {/* Description */}
              <section className="content-section">
                <h2>About this property</h2>
                <p>{property.description}</p>
              </section>

              {/* Amenities */}
              {property.amenities && property.amenities.length > 0 && (
                <section className="content-section">
                  <h2>Most popular facilities</h2>
                  <div className="amenities-grid-v2">
                    {property.amenities.map((amenity, index) => (
                      <div key={index} className="amenity-item-v2">
                        <span className="amenity-check">✓</span>
                        <span>{amenity.name}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Room Selection */}
              {property.rooms && property.rooms.length > 0 && (
                <section className="content-section rooms-table-section">
                  <h2>Availability</h2>
                  <div className="rooms-table">
                    <div className="table-header">
                      <div className="col-room-type">Room type</div>
                      <div className="col-sleeps">Sleeps</div>
                      <div className="col-price">Price</div>
                      <div className="col-select">Select rooms</div>
                    </div>
                    {property.rooms.map((room) => (
                      <div key={room.id} className="table-row">
                        <div className="col-room-type">
                          <h3>{room.name}</h3>
                          {room.description && (
                            <p className="room-desc">{room.description}</p>
                          )}
                          {room.amenities && room.amenities.length > 0 && (
                            <div className="room-features">
                              {room.amenities.slice(0, 3).map((amenity, idx) => (
                                <span key={idx} className="feature-badge">{amenity}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="col-sleeps">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M7 11c1.66 0 3-1.34 3-3S8.66 5 7 5 4 6.34 4 8s1.34 3 3 3zm0-4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm10 0c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0-4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM7 15c-2.21 0-4 1.79-4 4v2h8v-2c0-2.21-1.79-4-4-4zm-2 4c0-1.1.9-2 2-2s2 .9 2 2H5zm12-4c-2.21 0-4 1.79-4 4v2h8v-2c0-2.21-1.79-4-4-4zm-2 4c0-1.1.9-2 2-2s2 .9 2 2h-4z" fill="currentColor"/>
                          </svg>
                          <span>{room.maxGuests}</span>
                        </div>
                        <div className="col-price">
                          <div className="price-amount">PKR {room.price.toLocaleString()}</div>
                          <div className="price-info">per night</div>
                        </div>
                        <div className="col-select">
                          <select 
                            value={roomQuantities[room.id] || 0}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleRoomQuantityChange(room.id, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="room-select"
                            disabled={room.availableRooms === 0}
                          >
                            <option value="0">0</option>
                            {room.availableRooms > 0 && [...Array(Math.min(room.availableRooms, 20))].map((_, i) => (
                              <option key={i + 1} value={i + 1}>
                                {i + 1} {i + 1 === 1 ? 'room' : 'rooms'}
                              </option>
                            ))}
                          </select>
                          {room.availableRooms > 0 && (
                            <div className="availability-info">
                              {room.availableRooms} available
                            </div>
                          )}
                          {room.availableRooms === 0 && (
                            <div className="unavailable-info">Sold out</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Policies */}
              <section className="content-section">
                <h2>House rules</h2>
                <div className="policies-grid-v2">
                  <div className="policy-item-v2">
                    <h4>Check-in</h4>
                    <p>From {property.policies.checkIn}</p>
                  </div>
                  <div className="policy-item-v2">
                    <h4>Check-out</h4>
                    <p>Until {property.policies.checkOut}</p>
                  </div>
                  <div className="policy-item-v2">
                    <h4>Cancellation</h4>
                    <p>{property.policies.cancellation}</p>
                  </div>
                  <div className="policy-item-v2">
                    <h4>Children & beds</h4>
                    <p>{property.policies.children}</p>
                  </div>
                  <div className="policy-item-v2">
                    <h4>Pets</h4>
                    <p>{property.policies.pets}</p>
                  </div>
                </div>
              </section>
            </div>

            {/* Sidebar - Booking Card */}
            <aside className="sidebar-column">
              <div className="booking-card-v2">
                <div className="booking-card-inner">
                  <div className="booking-card-header">
                    <h3>Your booking details</h3>
                  </div>

                  <div className="date-selection">
                    <div className="date-input-group">
                      <label>Check-in</label>
                      <input 
                        type="date" 
                        value={checkIn}
                        onChange={(e) => setCheckIn(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="date-input-group">
                      <label>Check-out</label>
                      <input 
                        type="date" 
                        value={checkOut}
                        onChange={(e) => setCheckOut(e.target.value)}
                        min={checkIn}
                      />
                    </div>
                  </div>

                  <div className="guest-selection">
                    <label>Number of guests</label>
                    <select 
                      value={guests}
                      onChange={(e) => setGuests(Number(e.target.value))}
                      className="guest-select"
                    >
                      <option value={1}>1 guest</option>
                      <option value={2}>2 guests</option>
                      <option value={3}>3 guests</option>
                      <option value={4}>4 guests</option>
                      <option value={5}>5 guests</option>
                      <option value={6}>6 guests</option>
                    </select>
                  </div>

                  {/* Who is this booking for? */}
                  <div className="booking-for-section">
                    <label className="booking-for-label">Who is this booking for?</label>
                    <div className="booking-for-options">
                      <button
                        type="button"
                        className={`booking-for-btn${bookedForSelf ? ' active' : ''}`}
                        onClick={() => setBookedForSelf(true)}
                      >
                        Myself
                      </button>
                      <button
                        type="button"
                        className={`booking-for-btn${!bookedForSelf ? ' active' : ''}`}
                        onClick={() => setBookedForSelf(false)}
                      >
                        Someone else
                      </button>
                    </div>

                    {!bookedForSelf && (
                      <div className="guest-info-fields">
                        <div className="guest-info-field">
                          <label>Guest name <span className="required">*</span></label>
                          <input
                            type="text"
                            placeholder="Full name"
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            className="guest-info-input"
                          />
                        </div>
                        <div className="guest-info-field">
                          <label>Guest email <span className="required">*</span></label>
                          <input
                            type="email"
                            placeholder="Email address"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            className="guest-info-input"
                            required
                          />
                          <small style={{color:'#6366f1',fontSize:'12px',marginTop:'4px',display:'block'}}>
                            📧 A booking confirmation + login credentials will be emailed to this address.
                          </small>
                        </div>
                        <div className="guest-info-field">
                          <label>Guest phone</label>
                          <input
                            type="tel"
                            placeholder="Phone number"
                            value={guestPhone}
                            onChange={(e) => setGuestPhone(e.target.value)}
                            className="guest-info-input"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {totalRoomsSelected > 0 && (
                    <div className="booking-summary">
                      <div className="summary-row">
                        <span>Rooms selected:</span>
                        <span className="value">{totalRoomsSelected}</span>
                      </div>
                      <div className="summary-row total">
                        <span>Total price:</span>
                        <span className="value">PKR {totalPrice.toLocaleString()}</span>
                      </div>
                      <div className="summary-note">per night</div>
                    </div>
                  )}

                  {currentUser && (
                    <button 
                      className="reserve-btn"
                      onClick={handleBooking}
                      disabled={totalRoomsSelected === 0}
                    >
                      Reserve
                    </button>
                  )}
                  
                  {!currentUser && (
                    <button 
                      className="reserve-btn signin-btn"
                      onClick={handleBooking}
                    >
                      Sign in to Book
                    </button>
                  )}

                 {totalRoomsSelected === 0 && (
                    <div className="booking-note">
                      ℹ️ Select rooms from the table above
                    </div>
                  )}

                  {totalRoomsSelected > 0 && (
                    <div className="booking-benefits">
                      <p>✓ Free cancellation</p>
                      <p>✓ No payment today</p>
                      <p>✓ Confirmation is immediate</p>
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
